import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  ShieldCheck,
  IndianRupee,
  ArrowRight,
  User,
  Zap,
  Clock,
  Hourglass,
  RefreshCw,
  LayoutDashboard,
  Search,
  CheckCircle2,
} from 'lucide-react';
import { useSettings } from '../user/util/SettingsProvider';
const TrackPage = () => {
  const { trackId } = useParams();
  const navigate = useNavigate();

  const [num, setNum] = useState('');
  const [submittedNum, setSubmittedNum] = useState('');
  const [trackData, setTrackData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Offer details & inactive/not-found modal states
  const [offerData, setOfferData] = useState(null);
  const [isInactiveModalOpen, setIsInactiveModalOpen] = useState(false);
  const [isNotFoundModalOpen, setIsNotFoundModalOpen] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const settings = useSettings();
  
  useEffect(() => { 
    document.title = `Track${offerData?.name || trackData?.offer_name ? ` | ${offerData?.name || trackData?.offer_name}` : ''} | ${settings?.settings?.site_name || ''}`; 
  }, [settings?.settings?.site_name, offerData?.name, trackData?.offer_name]);

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  const getFallbackTelegramLink = useCallback(() => {
    try {
      const settings = JSON.parse(localStorage.getItem('settings') || '{}');
      return settings.tgLink || 'https://t.me';
    } catch (e) {
      return 'https://t.me';
    }
  }, []);

  const handleOfferNotFound = useCallback(() => {
    setIsNotFoundModalOpen(true);
    const tgLink = getFallbackTelegramLink();
    let count = 5;
    const timer = setInterval(() => {
      count -= 1;
      setRedirectCountdown(count);
      if (count <= 0) {
        clearInterval(timer);
        window.location.href = tgLink;
      }
    }, 1000);
  }, [getFallbackTelegramLink]);

  // Fetch Offer Details
  useEffect(() => {
    const fetchOfferTrackingDetails = async () => {
      try {
        setError(null);
        const token = getCookie('authToken');
        const baseUrl = process.env.REACT_APP_API_URL || '';

        const response = await axios.get(`${baseUrl}/api/public/track/${trackId}`, {
          params: { trackId: trackId },
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        const resData = response.data?.data || response.data;

        if (resData) {
          // Check for inactive status
          const status = String(resData.status || '').toLowerCase();
          if (status === 'inactive' || resData.isActive === false) {
            setIsInactiveModalOpen(true);
            const channelUrl = resData.channel || getFallbackTelegramLink();
            let count = 5;
            const timer = setInterval(() => {
              count -= 1;
              setRedirectCountdown(count);
              if (count <= 0) {
                clearInterval(timer);
                window.location.href = channelUrl;
              }
            }, 1000);
            return;
          }

          setOfferData(resData);
        } else {
          handleOfferNotFound();
        }
      } catch (err) {
        console.error('Error fetching offer tracking details:', err);
        handleOfferNotFound();
      } finally {
      }
    };

    if (trackId) {
      fetchOfferTrackingDetails();
    }
  }, [trackId, handleOfferNotFound, getFallbackTelegramLink]);

  const fetchTrackData = async (searchNum) => {
    try {
      setError(null);
      const token = getCookie('authToken');
      const baseUrl = process.env.REACT_APP_API_URL || '';

      const response = await axios.post(
        `${baseUrl}/api/track/track-lead`,
        {
          num: searchNum,
          trackId: trackId,
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      if (response.data?.status) {
        setTrackData(response.data.data);
      } else {
        setError(response.data?.msg || 'No record found for this reference.');
        setTrackData(null);
      }
    } catch (err) {
      console.error('Error fetching track data:', err);
      setError(
        err.response?.data?.msg ||
        err.response?.data?.message ||
        'No record found for this Reference / Track ID.'
      );
      setTrackData(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!num.trim()) return;

    setLoading(true);
    setSubmittedNum(num.trim());
    await fetchTrackData(num.trim());
    setLoading(false);
  };

  const handleRefresh = async () => {
    if (!submittedNum) return;
    setIsRefreshing(true);
    await fetchTrackData(submittedNum);
    setIsRefreshing(false);
  };

  const handleChangeUser = () => {
    setTrackData(null);
    setSubmittedNum('');
    setNum('');
    setError(null);
  };

  // Compute task status list
  const allEvents = trackData?.events || [];
  const completedEvents = trackData?.completed_events || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <div className="w-full bg-zinc-950 text-zinc-100 min-h-screen relative overflow-hidden font-sans selection:bg-emerald-500 selection:text-zinc-950 pb-24">
      <style>{`
        @keyframes springIn {
          0% { opacity: 0; transform: translateY(20px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes pulseGlow {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.45; transform: scale(1.1); }
        }

        @keyframes borderRotate {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        @keyframes floatEffect {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }

        @keyframes scaleIn {
          0% { opacity: 0; transform: scale(0.94); }
          100% { opacity: 1; transform: scale(1); }
        }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        @keyframes spinSlow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .animate-spring-in {
          animation: springIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .animate-scale-in {
          animation: scaleIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .ambient-glow {
          animation: pulseGlow 7s ease-in-out infinite;
        }

        .highlight-border {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.6) 0%, rgba(24, 24, 27, 0.9) 40%, rgba(20, 184, 166, 0.6) 100%);
          background-size: 200% 200%;
          animation: borderRotate 5s ease infinite;
        }

        .highlight-card-glow {
          box-shadow: 0 0 35px -5px rgba(16, 185, 129, 0.25), 0 20px 25px -5px rgba(0, 0, 0, 0.5);
        }

        .smooth-transition {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .micro-interaction {
          transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .micro-interaction:hover {
          transform: translateY(-2px);
        }

        .micro-interaction:active {
          transform: translateY(0px) scale(0.98);
        }

        .float-animation {
          animation: floatEffect 4s ease-in-out infinite;
        }

        .shimmer-bg {
          background: linear-gradient(90deg, rgba(39,39,42,0.6) 25%, rgba(63,63,70,0.6) 50%, rgba(39,39,42,0.6) 75%);
          background-size: 200% 100%;
          animation: shimmer 1.8s infinite;
        }

        .spin-slow {
          animation: spinSlow 1s linear infinite;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 8px;
        }
      `}</style>

      {/* Ambient background blur circles */}
      <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-emerald-500/15 rounded-full blur-[140px] pointer-events-none ambient-glow" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-teal-500/10 rounded-full blur-[120px] pointer-events-none ambient-glow" />

      <main className="max-w-[850px] mx-auto px-4 sm:px-8 pt-6 sm:pt-12 space-y-6 relative z-10">

        {/* Header Row */}
        <div className="flex items-center justify-between px-2 animate-spring-in">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-xs font-black text-zinc-400 hover:text-emerald-400 transition-colors uppercase tracking-widest micro-interaction"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="text-right">
            <p className="text-[10px] font-black text-zinc-500 uppercase tracking-tighter leading-none">
              Powered by
            </p>
            <p className="text-sm font-black text-emerald-400 uppercase italic leading-none">
              EarnMedia
            </p>
          </div>
        </div>

        {/* Offer Details Card */}
        <div className="bg-zinc-900/95 backdrop-blur-2xl rounded-[2.5rem] p-6 border border-zinc-800/80 shadow-2xl animate-spring-in smooth-transition hover:border-zinc-700">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-zinc-950 rounded-2xl flex items-center justify-center shadow-inner shrink-0 overflow-hidden border border-emerald-500/30 float-animation smooth-transition">
              {(offerData?.logo || trackData?.offer_logo) ? (
                <img
                  alt={offerData?.name || trackData?.offer_name || 'Offer'}
                  className="w-full h-full object-cover rounded-2xl"
                  src={offerData?.logo || trackData.offer_logo}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      'https://ui-avatars.com/api/?name=' +
                      encodeURIComponent(offerData?.name || trackData?.offer_name || 'Offer') +
                      '&background=059669&color=fff';
                  }}
                />
              ) : (
                <div className="w-full h-full bg-zinc-800/50 rounded-2xl shimmer-bg" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded-full text-[9px] font-black uppercase tracking-widest mb-1">
                <ShieldCheck className="w-3 h-3" />
                Active Campaign
              </div>
              <h1 className="text-xl sm:text-2xl font-black tracking-tight text-zinc-100 leading-tight truncate">
                {offerData?.name || trackData?.offer_name}
              </h1>
              <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest mt-1">
                Track Reference:{' '}
                <span className="text-emerald-400 font-mono">
                  #{trackId}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Search / Check Status Card */}
        {!trackData && !loading && (
          <div className="relative p-[2px] rounded-[2.5rem] highlight-border highlight-card-glow animate-scale-in">
            <div className="bg-zinc-900/95 backdrop-blur-2xl rounded-[2.4rem] p-8 border border-zinc-800/50 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:scale-110 smooth-transition pointer-events-none">
                <Search className="w-32 h-32 text-emerald-400" />
              </div>

              <div className="relative z-10">
                <h3 className="text-zinc-100 text-xl font-black mb-1">
                  Check Status
                </h3>
                <p className="text-zinc-400 text-xs font-medium mb-6">
                  Enter your registered UPI ID to see your earnings.
                </p>

                {error && (
                  <div className="mb-4 p-3.5 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400 text-xs font-medium flex items-center gap-2 animate-spring-in">
                    <span className="shrink-0">⚠️</span>
                    <span>{error}</span>
                  </div>
                )}

                <form
                  onSubmit={handleSubmit}
                  className="flex flex-col sm:flex-row gap-3"
                >
                  <div className="relative flex-1">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-400">
                      <IndianRupee className="w-4 h-4" />
                    </div>
                    <input
                      required
                      type="text"
                      placeholder="example@apl"
                      value={num}
                      onChange={(e) => setNum(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 bg-zinc-950/80 border border-zinc-800 rounded-2xl text-zinc-100 font-bold outline-none focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/15 smooth-transition text-sm placeholder:text-zinc-600"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-8 py-4 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-2xl font-black uppercase tracking-widest text-xs flex items-center justify-center gap-2 active:scale-95 smooth-transition micro-interaction shadow-lg shadow-emerald-500/25 disabled:opacity-50"
                  >
                    {loading ? 'Tracking...' : 'Track'}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Loading Skeleton */}
        {loading && (
          <div className="space-y-4 animate-scale-in">
            <div className="bg-zinc-900/80 rounded-[2rem] border border-zinc-800/80 p-6 shimmer-bg h-28" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-zinc-900/80 rounded-[2rem] border border-zinc-800/80 p-6 shimmer-bg h-28" />
              <div className="bg-zinc-900/80 rounded-[2rem] border border-zinc-800/80 p-6 shimmer-bg h-28" />
              <div className="bg-zinc-900/80 rounded-[2rem] border border-zinc-800/80 p-6 shimmer-bg h-28" />
            </div>
            <div className="bg-zinc-900/80 rounded-[2.5rem] border border-zinc-800/80 p-6 shimmer-bg h-64" />
          </div>
        )}

        {/* Result Section */}
        {trackData && !loading && (
          <div className="space-y-4 animate-scale-in">

            {/* Stat Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Tracking For */}
              <div className="sm:col-span-1 bg-zinc-900/95 backdrop-blur-2xl p-6 rounded-[2rem] border border-zinc-800/80 shadow-2xl flex flex-col justify-center items-center text-center smooth-transition hover:border-emerald-500/40 micro-interaction">
                <div className="w-10 h-10 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full flex items-center justify-center mb-2">
                  <User className="w-5 h-5" />
                </div>
                <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">
                  Tracking For
                </p>
                <p className="text-sm font-black text-zinc-100 truncate w-full">
                  {submittedNum}
                </p>
                <button
                  onClick={handleChangeUser}
                  className="text-[9px] font-bold text-emerald-400 uppercase mt-1 hover:underline smooth-transition"
                >
                  Change
                </button>
              </div>

              {/* Earnings */}
              <div className="bg-zinc-900/95 backdrop-blur-2xl p-6 rounded-[2rem] border border-zinc-800/80 shadow-2xl text-center smooth-transition hover:border-emerald-500/40 micro-interaction">
                <p className="text-2xl font-black text-emerald-400">
                  ₹{trackData.total_earned || 0}
                </p>
                <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mt-1">
                  Earnings
                </p>
              </div>

              {/* Conversions */}
              <div className="bg-zinc-900/95 backdrop-blur-2xl p-6 rounded-[2rem] border border-zinc-800/80 shadow-2xl text-center smooth-transition hover:border-emerald-500/40 micro-interaction">
                <p className="text-2xl font-black text-zinc-100">
                  {trackData.conversions || 0}
                </p>
                <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mt-1">
                  Conversions
                </p>
              </div>
            </div>

            {/* Task Status Card */}
            <div className="bg-zinc-900/95 backdrop-blur-2xl rounded-[2.5rem] border border-zinc-800/80 shadow-2xl overflow-hidden smooth-transition hover:border-zinc-700">
              {/* Header */}
              <div className="px-6 sm:px-8 py-5 border-b border-zinc-800/50 bg-zinc-950/50 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-emerald-400" />
                  <span className="font-black text-xs uppercase tracking-widest text-zinc-200">
                    Task Status
                  </span>
                </div>
                <div className="flex items-center gap-1 text-zinc-500">
                  <Clock className="w-3 h-3" />
                  <span className="text-[9px] font-bold uppercase">
                    {trackData.last_updated
                      ? formatDate(trackData.last_updated)
                      : 'Not Updated'}
                  </span>
                </div>
              </div>

              {/* Task List */}
              <div className="p-6 space-y-3">
                {allEvents.length === 0 && (
                  <p className="text-center text-xs text-zinc-500 font-medium py-4">
                    No tasks available for this campaign.
                  </p>
                )}

                {allEvents.map((event, idx) => {
                  const isCompleted = completedEvents.includes(event);
                  return (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-4 rounded-2xl border border-zinc-800/60 hover:border-emerald-500/40 bg-zinc-950/50 smooth-transition micro-interaction group"
                    >
                      <span className="text-sm font-bold text-zinc-300 capitalize group-hover:text-emerald-400 smooth-transition">
                        {event}
                      </span>

                      {isCompleted ? (
                        <span className="flex items-center gap-1 px-3 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full text-[10px] font-black uppercase">
                          <CheckCircle2 className="w-3 h-3" />
                          Completed
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-full text-[10px] font-black uppercase animate-pulse">
                          <Hourglass className="w-3 h-3" />
                          Pending
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div className="p-6 pt-0 grid grid-cols-2 gap-3">
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="py-4 bg-zinc-900 border border-zinc-700/80 hover:border-emerald-500/50 text-zinc-100 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2 active:scale-95 smooth-transition micro-interaction disabled:opacity-50"
                >
                  <RefreshCw
                    className={`w-3.5 h-3.5 ${isRefreshing ? 'spin-slow' : ''}`}
                  />
                  {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </button>
                <button
                  onClick={() => navigate(`/refer/${trackId}`)}
                  className="py-4 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2 active:scale-95 smooth-transition micro-interaction shadow-lg shadow-emerald-500/20"
                >
                  <LayoutDashboard className="w-3.5 h-3.5" />
                  Refer Track
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* OFFER INACTIVE MODAL */}
      {isInactiveModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-spring-in">
          <div className="relative w-full max-w-md bg-zinc-900 border border-amber-500/30 rounded-[2.5rem] shadow-2xl overflow-hidden p-8 text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center justify-center mx-auto float-animation">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-black text-zinc-100 tracking-tight">Offer Currently Inactive</h3>
              <p className="text-xs text-zinc-400 font-medium leading-relaxed">
                This campaign is temporarily paused or completed. Redirecting you to our official channel for active updates...
              </p>
            </div>

            <div className="p-4 bg-zinc-950 rounded-2xl border border-zinc-800">
              <p className="text-amber-400 text-xs font-mono font-bold">
                Redirecting to Channel in {redirectCountdown}s
              </p>
            </div>

            <button
              onClick={() => window.location.href = offerData?.channel || getFallbackTelegramLink()}
              className="w-full py-3.5 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-black rounded-xl text-xs uppercase tracking-wider smooth-transition micro-interaction shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2"
            >
              <span>Go to Channel Now</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* OFFER NOT FOUND MODAL */}
      {isNotFoundModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-spring-in">
          <div className="relative w-full max-w-md bg-zinc-900 border border-rose-500/30 rounded-[2.5rem] shadow-2xl overflow-hidden p-8 text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20 flex items-center justify-center mx-auto float-animation">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-black text-zinc-100 tracking-tight">Offer Not Found</h3>
              <p className="text-xs text-zinc-400 font-medium leading-relaxed">
                The tracking link you accessed could not be resolved or has expired. Redirecting you to our updates channel...
              </p>
            </div>

            <div className="p-4 bg-zinc-950 rounded-2xl border border-zinc-800">
              <p className="text-rose-400 text-xs font-mono font-bold">
                Redirecting in {redirectCountdown}s
              </p>
            </div>

            <button
              onClick={() => window.location.href = getFallbackTelegramLink()}
              className="w-full py-3.5 bg-rose-500 hover:bg-rose-400 text-zinc-950 font-black rounded-xl text-xs uppercase tracking-wider smooth-transition micro-interaction shadow-lg shadow-rose-500/20 flex items-center justify-center gap-2"
            >
              <span>Join Updates Channel</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default TrackPage;